import { Component, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ChartModule } from 'primeng/chart';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { MerchantSummary, StoreEdge, StoreEdgeUptime, StoreEdgeUsage, StoreEdgeUsagePoint, XtExportSwitch } from '../../core/models';
import { EdgeInstaller } from './edge-installer';
import { LayoutService } from '../../layout/service/layout.service';
import { EdgeDevices } from './edge-devices';
import { edgeStatusLabel, edgeStatusSeverity, edgeStatusTitle, relativeTime } from './store-edge-status';

// Often enough that a store going quiet is noticed while the operator is looking at the page, rarely enough
// that a screen left open all day is not a load. An Edge beats every 30s, so a tick can move a row at most
// one beat late.
const REFRESH_MS = 20_000;

// Same approach as the courier-costs charts: fixed data-viz colours stepped per surface rather than the theme's
// accent ramp. One hue per measure; the peak line is the same hue drawn thin and dashed, so it reads as context
// for the average rather than as a second thing.
// Average and Peak are SEPARATE COLOURS, not one colour told apart by a dash. The legend is drawn with
// usePointStyle, so every entry is a filled circle and the dash never reaches it - two identical dots labelled
// Average and Peak. They stay within one hue per metric (CPU blue, memory purple) so a glance still reads the
// chart as one measurement, and the peak is the lighter of the pair because it is the fainter line.
const USAGE_COLORS = {
    light: {
        cpu: '#2a78d6', cpuPeak: '#79b0ee', memory: '#7c4dcc', memoryPeak: '#b498e8',
        read: '#1f9d6b', write: '#eb6834', grid: '#e1e0d9', ink: '#52514e', muted: '#898781'
    },
    dark: {
        cpu: '#3987e5', cpuPeak: '#8fc0f2', memory: '#9b74e0', memoryPeak: '#c3aaef',
        read: '#34b886', write: '#d95926', grid: '#2c2c2a', ink: '#c3c2b7', muted: '#898781'
    }
};
const CHART_FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

// WHERE THE EDGES ARE, AND WHICH OF THEM ARE UP. Every machine that has registered itself against a
// location, so an operator can see a store go quiet without phoning it.
//
// The list is NOT re-sorted here: the API returns it offline-first, then longest-silent, which is the order
// the rows need attention in. Sorting it in the browser would only put the healthy stores back on top.
@Component({
    selector: 'app-edges',
    standalone: true,
    imports: [FormsModule, RouterLink, DatePipe, CardModule, ButtonModule, TableModule, TagModule, MessageModule, SelectModule, DialogModule, TooltipModule, ChartModule, EdgeDevices, EdgeInstaller],
    template: `
        <!-- Machines that enrolled themselves and are waiting on a human, above the ones already installed: a
             device waiting to be let in is the only thing on this page that asks the operator for something. -->
        <app-edge-devices />

        <!-- The package that puts an Edge on a till, for THIS environment. It sits between the queue of machines
             waiting to be let in and the machines already running, because that is the order a technician
             works in: install, then approve, then watch it come up. -->
        <app-edge-installer />

        <p-card>
            <ng-template pTemplate="title"><span>Store Edges</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Online: heard from in the last 30 seconds. Recent: within 5 minutes. Offline: longer than that." tooltipPosition="top"></i></ng-template>

            <!-- THE STOP ON EVERY STORE'S EXATOUCH EXPORT. It lives on this page because this is where the stores
                 are, and it is one line and one button because the moment it is needed is not a moment for
                 reading. Stopped is loud on purpose. -->
            @if (xtSwitch(); as sw) {
                <div class="flex flex-wrap items-center gap-2 mb-3 p-2 rounded border"
                     [class.border-red-400]="sw.stopped" [class.bg-red-50]="sw.stopped" [class.dark:bg-red-950]="sw.stopped"
                     [class.border-surface-200]="!sw.stopped" [class.dark:border-surface-700]="!sw.stopped">
                    <i class="pi" [class.pi-ban]="sw.stopped" [class.pi-check-circle]="!sw.stopped"
                       [class.text-red-500]="sw.stopped" [class.text-green-500]="!sw.stopped"></i>
                    <span class="text-sm">
                        <b>Exatouch export:</b>
                        @if (sw.stopped) {
                            <span class="text-red-600 dark:text-red-400 font-semibold">stopped at every store</span>
                            @if (sw.changedBy) { by {{ sw.changedBy }} }
                            @if (sw.changedAt) { {{ since(sw.changedAt) }} }
                            @if (sw.reason) { — <i>{{ sw.reason }}</i> }
                        } @else {
                            running
                            @if (sw.changedAt) { <span class="text-surface-500">(allowed again by {{ sw.changedBy }} {{ since(sw.changedAt) }})</span> }
                        }
                    </span>
                    <span class="flex-1"></span>
                    @if (sw.stopped) {
                        <p-button label="Allow again" icon="pi pi-play" size="small" severity="secondary" [outlined]="true"
                                  (onClick)="openSwitch(false)"
                                  pTooltip="Every store starts writing again on its next heartbeat, including everything it queued while stopped." tooltipPosition="left" />
                    } @else {
                        <p-button label="Stop every store" icon="pi pi-ban" size="small" severity="danger" [outlined]="true"
                                  (onClick)="openSwitch(true)"
                                  pTooltip="Tells every Edge to stop writing orders into Exatouch. Reaches each store within seconds; orders are queued at the store, not lost." tooltipPosition="left" />
                    }
                </div>
            }

            <!-- The thresholds are spelled out once, here, so the badges can stay single words. -->

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchants()" optionLabel="name" optionValue="merchantId"
                          [ngModel]="merchantId()" (ngModelChange)="onMerchantChange($event ?? null)"
                          placeholder="All merchants" [showClear]="true" [filter]="true" styleClass="w-full sm:w-64" />
                <p-button icon="pi pi-refresh" [text]="true" size="small" [loading]="refreshing()" (onClick)="refresh()" />
            </div>

            @if (merchantsError()) {
                <p-message severity="warn" styleClass="mb-3">{{ merchantsError() }}</p-message>
            }
            <!-- BESIDE the table, never instead of it: a failed refresh leaves the last good read on screen,
                 because "the middleware could not be reached" is not the same news as "no store has an Edge". -->
            @if (error()) {
                <p-message severity="error" styleClass="mb-3">{{ error() }}</p-message>
            }
            @if (note()) {
                <p-message severity="success" styleClass="mb-3">{{ note() }}</p-message>
            }

            <p-table [value]="edges()" [loading]="loading()" [paginator]="edges().length > 15" [rows]="15">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width: 8rem">Status</th>
                        <th>Machine</th>
                        <th>Location</th>
                        <th>Merchant</th>
                        <th>Last seen</th>
                        <th>Version</th>
                        <th>CPU</th>
                        <th>Memory</th>
                        <th>Registered</th>
                        <th style="width: 6rem"></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-e>
                    <tr>
                        <td>
                            <p-tag [value]="statusLabel(e)" [severity]="statusSeverity(e)" [attr.title]="statusTitle(e)" />
                        </td>
                        <td class="font-medium" [attr.title]="e.lastSeenAddress ? 'Last seen from ' + e.lastSeenAddress : null">
                            {{ e.machineName }}
                        </td>
                        <td>
                            <a class="text-primary hover:underline" [routerLink]="['/locations', e.locationId]">{{ e.locationName }}</a>
                        </td>
                        <td>
                            <a class="text-primary hover:underline" [routerLink]="['/merchants', e.merchantId]">{{ e.merchantName }}</a>
                        </td>
                        <!-- Coarse on the page, exact on hover. No seconds: whether to act is already the
                             Status badge's job, and a column ticking up second by second only invites the
                             operator to watch it. The timestamp is what they quote to the store. -->
                        <td class="text-sm whitespace-nowrap" [attr.title]="e.lastSeenAt ? (e.lastSeenAt | date: 'medium') : 'Never heard from'">
                            {{ e.lastSeenAt ? since(e.lastSeenAt) : 'Never' }}
                        </td>
                        <td class="text-sm whitespace-nowrap">{{ e.lastSeenVersion ?? '—' }}</td>
                        <td class="text-sm whitespace-nowrap" [class.text-red-500]="(e.cpuPercent ?? 0) >= 90">
                            {{ cpuText(e) }}
                        </td>
                        <td class="text-sm whitespace-nowrap" [class.text-red-500]="memoryPercent(e) >= 90">
                            {{ memoryText(e) }}
                        </td>
                        <td class="text-sm whitespace-nowrap" [attr.title]="e.registeredAt | date: 'medium'">
                            {{ e.registeredBy }} · {{ since(e.registeredAt) }}
                        </td>
                        <td>
                            <div class="flex justify-end">
                                <p-button icon="pi pi-chart-bar" [text]="true" size="small"
                                          ariaLabel="Uptime for this Edge" pTooltip="Uptime"
                                          (onClick)="openUptime(e)" />
                                <p-button icon="pi pi-trash" [text]="true" size="small" severity="danger"
                                          ariaLabel="Retire this Edge" (onClick)="openRetire(e)" />
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="10">
                            {{ error()
                                ? 'The Edges could not be read — the banner above says why.'
                                : 'No store has registered an Edge yet.' }}
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>

        <!-- Retiring an installation is not deleting a store, and the confirmation says which of the two this
             is: the machine loses its key and comes back through the approval queue, while everything the
             location owns stays. -->
        <p-dialog header="Retire this Edge" [(visible)]="retireOpen" [modal]="true"
                  [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            @if (retireTarget(); as target) {
                <p class="text-sm mb-3">
                    Retire the Edge on <b>{{ target.machineName }}</b> at <b>{{ target.locationName }}</b>? Its key
                    stops working at once and its orders stop. The machine enrols again on its own within a minute
                    and appears under Edge devices for approval; nobody has to visit the store.
                </p>
            }
            @if (retireError()) { <p-message severity="error">{{ retireError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="retiring()" (onClick)="retireOpen.set(false)" />
                <p-button label="Retire Edge" icon="pi pi-trash" severity="danger" [loading]="retiring()" (onClick)="confirmRetire()" />
            </ng-template>
        </p-dialog>

        <p-dialog [header]="switchTo() ? 'Stop the Exatouch export at every store' : 'Allow the Exatouch export again'"
                  [(visible)]="switchOpen" [modal]="true" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '95vw' }">
            @if (switchTo()) {
                <p class="text-sm mb-3">
                    Every Edge stops writing orders into its store's Exatouch within seconds. Nothing is lost: each
                    store keeps queueing what its counter takes and writes all of it the moment the export is
                    allowed again. Say why, so the next person to see this knows whether it is safe to lift.
                </p>
            } @else {
                <p class="text-sm mb-3">
                    Every store starts writing again on its next heartbeat — including everything it queued while
                    stopped. If the reason for the stop was a bad build, make sure the stores have taken the fixed one
                    first.
                </p>
            }
            <textarea rows="2" [ngModel]="switchReason()" (ngModelChange)="switchReason.set($event)"
                      class="w-full text-sm p-2 border border-surface-300 dark:border-surface-700 rounded bg-surface-0 dark:bg-surface-900"
                      [placeholder]="switchTo() ? 'Why (shown beside the switch)' : 'Note (optional)'"></textarea>
            @if (switchError()) { <p-message severity="error" styleClass="mt-3">{{ switchError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="switching()" (onClick)="switchOpen.set(false)" />
                <p-button [label]="switchTo() ? 'Stop every store' : 'Allow again'" [icon]="switchTo() ? 'pi pi-ban' : 'pi pi-play'"
                          [severity]="switchTo() ? 'danger' : 'primary'" [loading]="switching()" (onClick)="confirmSwitch()" />
            </ng-template>
        </p-dialog>

        <!-- HOW THE STORE HAS BEEN. The list says whether the Edge is up now; this says whether it was up
             while nobody was looking. Only the stretches it was UP are recorded, so every gap drawn here is
             derived rather than stored — which is why an outage needs no job to notice it in order to
             exist, and why a store that was trading fine behind a broken link can fill its own gap in
             later without anything rewriting history. -->
        <p-dialog header="Uptime and performance" [(visible)]="uptimeOpen" [modal]="true"
                  [style]="{ width: '54rem' }" [breakpoints]="{ '860px': '95vw' }">
            @if (uptimeTarget(); as target) {
                <p class="text-sm mb-3">
                    <b>{{ target.machineName }}</b> at <b>{{ target.locationName }}</b>
                    <i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Green is time the Edge was up. Gaps are time the store was offline." tooltipPosition="top"></i>
                </p>
            }

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="windows" optionLabel="label" optionValue="hours"
                          [ngModel]="windowHours()" (ngModelChange)="setWindow($event)" styleClass="w-48" />
                <p-button icon="pi pi-refresh" [text]="true" size="small"
                          [loading]="uptimeLoading()" (onClick)="loadUptime()" />
            </div>

            @if (uptimeError()) { <p-message severity="error">{{ uptimeError() }}</p-message> }

            @if (uptime(); as u) {
                <div class="flex items-baseline gap-3 mb-2">
                    <span class="text-3xl font-semibold">{{ u.onlinePercent }}%</span>
                    <span class="text-sm text-surface-500 dark:text-surface-400">
                        up · {{ humanise(u.onlineSeconds) }} of {{ humanise(u.windowSeconds) }}
                    </span>
                </div>

                <div class="relative h-8 w-full rounded overflow-hidden bg-red-200 dark:bg-red-900/40">
                    @for (b of bars(); track $index) {
                        <div class="absolute top-0 h-full bg-green-500" [class.opacity-80]="b.open"
                             [style.left.%]="b.left" [style.width.%]="b.width" [attr.title]="b.title"></div>
                    }
                </div>
                <div class="flex justify-between text-xs text-surface-500 dark:text-surface-400 mt-1">
                    <span>{{ u.from | date: 'short' }}</span>
                    <span>{{ u.to | date: 'short' }}</span>
                </div>

                <!-- Under the same window as the bar and on the same time axis, so a slow afternoon reads straight
                     down against whether the store was even up. -->
                @if (usage(); as us) {
                    @if (us.points.length) {
                        <h4 class="text-sm font-medium mt-5 mb-1">{{ cpuTitle(us) }}</h4>
                        <p-chart type="line" [data]="cpuChart()" [options]="percentOptions()" height="9rem" />
                        <h4 class="text-sm font-medium mt-4 mb-1">{{ memoryTitle(us) }}</h4>
                        <p-chart type="line" [data]="memoryChart()" [options]="percentOptions()" height="9rem" />
                        <h4 class="text-sm font-medium mt-4 mb-1">Disk</h4>
                        <p-chart type="line" [data]="diskChart()" [options]="diskOptions()" height="9rem" />
                    } @else {
                        <p class="text-sm text-surface-500 dark:text-surface-400 mt-4">No performance data for this period.</p>
                    }
                }

                <!-- The bar shows the shape; this says what to quote to the store. Blips under a minute are
                     left out: a single missed beat is not an outage, and listing it would bury the real one. -->
                <h4 class="text-sm font-medium mt-4 mb-1">
                    {{ outages().length ? outages().length + ' outage(s) over a minute' : 'No outage over a minute' }}
                </h4>
                @if (outages().length) {
                    <ul class="text-sm list-none p-0 m-0 max-h-52 overflow-y-auto">
                        @for (o of outages(); track $index) {
                            <li class="flex justify-between gap-4 py-1 border-b border-surface-200 dark:border-surface-700">
                                <span>{{ o.start | date: 'short' }}</span>
                                <span class="text-surface-500 dark:text-surface-400">dark for {{ humanise(o.seconds) }}</span>
                            </li>
                        }
                    </ul>
                }

                <p class="text-xs text-surface-500 dark:text-surface-400 mt-3">
                    Last heard from
                    {{ u.lastSeenAt ? (u.lastSeenAt | date: 'medium') : 'never — this Edge registered and was never started' }}.
                </p>
            }
        </p-dialog>
    `
})
export class StoreEdges implements OnInit, OnDestroy {
    private readonly api = inject(MiddlewareApi);
    private readonly layout = inject(LayoutService);

    readonly refreshSeconds = REFRESH_MS / 1000;

    readonly edges = signal<StoreEdge[]>([]);
    // `loading` drives the table's own spinner and is only ever true before the first answer — a tick that
    // greyed the table every 20 seconds would make a page nobody touched look busy.
    readonly loading = signal(true);
    readonly refreshing = signal(false);
    readonly error = signal<string | null>(null);
    readonly note = signal<string | null>(null);
    private loadSub?: Subscription;
    private timer: ReturnType<typeof setInterval> | null = null;

    readonly merchantId = signal<string | null>(null);
    readonly merchants = signal<MerchantSummary[]>([]);
    readonly merchantsError = signal<string | null>(null);

    readonly retireOpen = signal(false);
    readonly retireTarget = signal<StoreEdge | null>(null);
    readonly retireError = signal<string | null>(null);
    readonly retiring = signal(false);

    // The platform-wide stop on the Exatouch export. Null until first read, so the strip is not drawn as
    // "running" for the second before the answer arrives - a stopped estate must never flash as running.
    readonly xtSwitch = signal<XtExportSwitch | null>(null);
    readonly switchOpen = signal(false);
    /** What the dialog is about to do: true stops, false allows again. */
    readonly switchTo = signal(false);
    readonly switchReason = signal<string | null>(null);
    readonly switchError = signal<string | null>(null);
    readonly switching = signal(false);

    // Template helpers — the wording lives beside the model, shared with the location page's line.
    readonly statusLabel = edgeStatusLabel;
    readonly statusSeverity = edgeStatusSeverity;
    readonly statusTitle = edgeStatusTitle;
    readonly since = relativeTime;

    // Both cards move on ONE tick. A second interval for the devices card would put the two out of step, and an
    // operator watching an install would see the queue and the Edges list disagree about the same machine.
    readonly deviceCard = viewChild(EdgeDevices);

    ngOnInit(): void {
        this.load();
        this.timer = setInterval(() => {
            this.load();
            this.deviceCard()?.reload();
            // An open dialog follows the same tick. Somebody who picked "last 30 minutes" is watching a machine
            // right now, and a chart that only moved when they pressed refresh would be showing them the past.
            if (this.uptimeOpen()) this.loadUptime();
        }, REFRESH_MS);
        this.api.getMerchants().subscribe({
            next: (m) => this.merchants.set(m ?? []),
            error: (e) => this.merchantsError.set(this.describe(e, 'merchants:read'))
        });
    }

    ngOnDestroy(): void {
        // A page the operator navigated away from must stop polling; so must a tab left open overnight.
        if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
        this.loadSub?.unsubscribe();
        this.uptimeSub?.unsubscribe();
        this.usageSub?.unsubscribe();
    }

    onMerchantChange(merchantId: string | null): void {
        this.merchantId.set(merchantId);
        // A different question, so the old answer is not kept: these rows belong to the merchant that was
        // selected a moment ago, not to this one.
        this.edges.set([]);
        this.loading.set(true);
        this.load();
    }

    refresh(): void {
        this.note.set(null);
        this.load();
    }

    openSwitch(stop: boolean): void {
        this.switchTo.set(stop);
        this.switchReason.set(null);
        this.switchError.set(null);
        this.switchOpen.set(true);
    }

    confirmSwitch(): void {
        const stop = this.switchTo();
        this.switching.set(true);
        this.switchError.set(null);
        this.api.setXtExportSwitch(stop, this.switchReason()).subscribe({
            next: (sw) => {
                this.switching.set(false);
                this.switchOpen.set(false);
                this.xtSwitch.set(sw);
                this.note.set(stop
                    ? 'Every store\'s Exatouch export is stopped. Each Edge hears it on its next heartbeat.'
                    : 'The Exatouch export is allowed again. Each store writes what it queued on its next heartbeat.');
            },
            error: (e) => { this.switching.set(false); this.switchError.set(this.describe(e, 'edges:manage')); }
        });
    }

    openRetire(edge: StoreEdge): void {
        this.retireTarget.set(edge);
        this.retireError.set(null);
        this.retireOpen.set(true);
    }

    confirmRetire(): void {
        const target = this.retireTarget();
        if (!target) return;
        this.retiring.set(true);
        this.retireError.set(null);
        this.api.deleteStoreEdge(target.edgeId).subscribe({
            next: () => {
                this.retiring.set(false);
                this.retireOpen.set(false);
                this.note.set(`Retired the Edge on ${target.machineName} — it will enrol again on its own; approve it under Edge devices.`);
                this.load();
            },
            error: (e) => { this.retiring.set(false); this.retireError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    private load(): void {
        // A tick that lands while the previous one is still in flight replaces it, so a slow middleware
        // cannot leave requests piling up behind it.
        this.loadSub?.unsubscribe();
        this.refreshing.set(true);
        // The switch rides the same tick as the list: an operator who has just pulled it in another tab must see
        // it here without pressing anything. A failed read leaves the last answer standing, like the rows do.
        this.api.getXtExportSwitch().subscribe({ next: (sw) => this.xtSwitch.set(sw), error: () => {} });
        this.loadSub = this.api.getStoreEdges(this.merchantId()).subscribe({
            next: (edges) => {
                this.edges.set(edges ?? []);
                this.error.set(null);
                this.loading.set(false);
                this.refreshing.set(false);
            },
            // The rows stay: a refresh that failed says nothing about the stores, only about this request,
            // and emptying the table would report every Edge as gone.
            error: (e) => {
                this.error.set(this.describe(e, 'merchants:read'));
                this.loading.set(false);
                this.refreshing.set(false);
            }
        });
    }


    // ---- uptime ----
    // Shortest first, the way a time-range picker reads everywhere else. The short ones are for watching a problem
    // while it is happening; the history is kept in five-minute buckets, so half an hour is six points plus the
    // one still filling - coarse, and honest about it, rather than a smooth line invented between readings.
    readonly windows = [
        { label: 'Last 30 minutes', hours: 0.5 },
        { label: 'Last hour', hours: 1 },
        { label: 'Last 3 hours', hours: 3 },
        { label: 'Last 6 hours', hours: 6 },
        { label: 'Last 12 hours', hours: 12 },
        { label: 'Last 24 hours', hours: 24 },
        { label: 'Last 7 days', hours: 24 * 7 },
        { label: 'Last 30 days', hours: 24 * 30 }
    ];

    readonly uptimeOpen = signal(false);
    readonly uptimeTarget = signal<StoreEdge | null>(null);
    readonly uptime = signal<StoreEdgeUptime | null>(null);
    readonly uptimeLoading = signal(false);
    readonly uptimeError = signal<string | null>(null);
    readonly windowHours = signal(24);
    private uptimeSub?: Subscription;

    /** The green blocks, as percentages of the window, so the bar needs no measuring in the template. */
    readonly bars = computed(() => {
        const u = this.uptime();
        if (!u) return [];
        const from = Date.parse(u.from);
        const span = Date.parse(u.to) - from;
        if (!(span > 0)) return [];

        return u.periods.map((p) => {
            const start = Math.max(from, Date.parse(p.startedAt));
            const end = Math.min(from + span, Date.parse(p.endedAt));
            return {
                left: ((start - from) / span) * 100,
                // A ten-minute stretch inside a thirty-day window rounds to nothing. A hairline still
                // says "it was up here", which is the whole reason for drawing the bar.
                width: Math.max(0.3, ((end - start) / span) * 100),
                open: p.open,
                title: `${new Date(start).toLocaleString()} → ${p.open ? 'now' : new Date(end).toLocaleString()}`
            };
        });
    });

    /** Derived from the gaps between stretches, plus either end of the window. */
    readonly outages = computed(() => {
        const u = this.uptime();
        if (!u) return [];
        const from = Date.parse(u.from);
        const to = Date.parse(u.to);

        const gaps: { start: Date; seconds: number }[] = [];
        let cursor = from;
        for (const p of u.periods) {
            const start = Date.parse(p.startedAt);
            if (start > cursor) gaps.push({ start: new Date(cursor), seconds: (start - cursor) / 1000 });
            cursor = Math.max(cursor, Date.parse(p.endedAt));
        }
        if (cursor < to) gaps.push({ start: new Date(cursor), seconds: (to - cursor) / 1000 });

        // One missed beat is not an outage. Listing it would bury the one that matters.
        return gaps.filter((g) => g.seconds >= 60);
    });

    memoryPercent(e: StoreEdge): number {
        return e.memoryTotalMb && e.memoryUsedMb !== null ? Math.round((e.memoryUsedMb / e.memoryTotalMb) * 100) : 0;
    }

    /** "24.7% of 16 cores". The percentage alone hides whether that is a busy till or an idle server. */
    cpuText(e: StoreEdge): string {
        if (e.cpuPercent === null || e.cpuPercent === undefined) return e.cpuCores ? `— of ${e.cpuCores} cores` : '—';
        return e.cpuCores ? `${e.cpuPercent}% of ${e.cpuCores} cores` : `${e.cpuPercent}%`;
    }

    /** "71% · 46.4 of 63.7 GB". */
    memoryText(e: StoreEdge): string {
        if (!e.memoryTotalMb || e.memoryUsedMb === null) return '—';
        return `${this.memoryPercent(e)}% · ${this.gb(e.memoryUsedMb)} of ${this.gb(e.memoryTotalMb)} GB`;
    }

    gb(mb: number | null): string {
        return mb === null ? '—' : (mb / 1024).toFixed(1);
    }

    openUptime(edge: StoreEdge): void {
        this.uptimeTarget.set(edge);
        this.uptime.set(null);
        this.usage.set(null);
        this.uptimeError.set(null);
        this.uptimeOpen.set(true);
        this.loadUptime();
    }

    setWindow(hours: number): void {
        this.windowHours.set(hours);
        this.loadUptime();
    }

    loadUptime(): void {
        const target = this.uptimeTarget();
        if (!target) return;
        this.uptimeSub?.unsubscribe();
        this.uptimeLoading.set(true);
        this.uptimeError.set(null);

        const to = new Date();
        const from = new Date(to.getTime() - this.windowHours() * 3_600_000);

        // Beside the uptime, not after it, and allowed to fail on its own: a server that predates the history
        // answers 404 here, and that must cost the operator the charts and nothing else.
        this.usageSub?.unsubscribe();
        this.usageSub = this.api.getStoreEdgeUsage(target.edgeId, from.toISOString(), to.toISOString()).subscribe({
            next: (u) => this.usage.set(u),
            error: () => this.usage.set(null)
        });
        this.uptimeSub = this.api
            .getStoreEdgeUptime(target.edgeId, from.toISOString(), to.toISOString())
            .subscribe({
                next: (u) => { this.uptime.set(u); this.uptimeLoading.set(false); },
                error: (e) => {
                    this.uptimeError.set(this.describe(e, 'merchants:read'));
                    this.uptimeLoading.set(false);
                }
            });
    }

    // ---- performance ----
    readonly usage = signal<StoreEdgeUsage | null>(null);
    private usageSub?: Subscription;

    /** Charts rebuild when the theme flips: Chart.js paints to a canvas, so a repaint is how colours follow it. */
    private readonly palette = computed(() => (this.layout.isDarkTheme() ? USAGE_COLORS.dark : USAGE_COLORS.light));

    /**
     * One series as x/y points, with a null dropped in wherever two buckets are not neighbours.
     *
     * The null is the point: it BREAKS the line. A stroke drawn straight across two silent hours would claim the
     * machine sat at that load while it was switched off.
     */
    private line(pick: (p: StoreEdgeUsagePoint) => number | null): { x: number; y: number | null }[] {
        const u = this.usage();
        if (!u) return [];
        const gap = u.bucketSeconds * 2000;
        const out: { x: number; y: number | null }[] = [];
        let previous: number | null = null;
        for (const p of u.points) {
            const x = Date.parse(p.bucketStart);
            if (previous !== null && x - previous >= gap) out.push({ x: previous + 1, y: null });
            out.push({ x, y: pick(p) });
            previous = x;
        }
        return out;
    }

    private dataset(label: string, color: string, data: { x: number; y: number | null }[], faint = false) {
        return {
            label, data, borderColor: color, backgroundColor: color,
            borderWidth: faint ? 1 : 1.75, borderDash: faint ? [4, 3] : [],
            // THE LEGEND DRAWS THIS SWATCH, so it is a line rather than the default circle: Chart.js applies
            // the dataset's borderDash to the legend item, and a circle has nowhere to show it. Peak is
            // therefore dashed in the key exactly as it is in the plot. Points are never drawn (radius 0),
            // so this only affects the key.
            pointStyle: 'line',
            pointRadius: 0, pointHitRadius: 6, tension: 0.2, spanGaps: false
        };
    }

    readonly cpuChart = computed(() => {
        const c = this.palette();
        return { datasets: [
            this.dataset('Average', c.cpu, this.line((p) => p.cpuAvgPercent)),
            this.dataset('Peak', c.cpuPeak, this.line((p) => p.cpuMaxPercent), true)
        ] };
    });

    /** As a share of what the machine has, so the scale is the same 0 to 100 as CPU and a full PC looks full. */
    readonly memoryChart = computed(() => {
        const c = this.palette();
        const share = (used: number | null, total: number | null) =>
            used !== null && total ? Math.round((used / total) * 1000) / 10 : null;
        return { datasets: [
            this.dataset('Average', c.memory, this.line((p) => share(p.memoryAvgUsedMb, p.memoryTotalMb))),
            this.dataset('Peak', c.memoryPeak, this.line((p) => share(p.memoryMaxUsedMb, p.memoryTotalMb)), true)
        ] };
    });

    readonly diskChart = computed(() => {
        const c = this.palette();
        return { datasets: [
            this.dataset('Read', c.read, this.line((p) => p.diskReadAvgKbps)),
            this.dataset('Write', c.write, this.line((p) => p.diskWriteAvgKbps))
        ] };
    });

    readonly percentOptions = computed(() => this.chartOptions((v) => `${Math.round(v)}%`, 100));
    readonly diskOptions = computed(() => this.chartOptions(this.throughput));

    /**
     * The X axis is the WINDOW, not the data. A chart that stretched to fit its points would hide the hours a store
     * sent nothing, and it would stop lining up with the uptime bar above it.
     */
    private chartOptions(format: (v: number) => string, max?: number) {
        const c = this.palette();
        const from = Date.parse(this.uptime()?.from ?? this.usage()?.from ?? '');
        const to = Date.parse(this.uptime()?.to ?? this.usage()?.to ?? '');
        const long = to - from > 36 * 3_600_000;
        const stamp = (ms: number) => new Date(ms).toLocaleString(undefined, long
            ? { month: 'short', day: 'numeric' }
            : { hour: '2-digit', minute: '2-digit' });
        return {
            maintainAspectRatio: false,
            animation: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { position: 'bottom', labels: { color: c.ink, usePointStyle: true, boxWidth: 18, font: { family: CHART_FONT } } },
                tooltip: {
                    callbacks: {
                        title: (items: any[]) => (items.length ? new Date(items[0].parsed.x).toLocaleString() : ''),
                        label: (ctx: any) => `${ctx.dataset.label}: ${format(Number(ctx.parsed.y))}`
                    }
                }
            },
            scales: {
                x: {
                    type: 'linear', min: from, max: to,
                    ticks: { color: c.muted, font: { family: CHART_FONT }, maxTicksLimit: 8, callback: (v: any) => stamp(Number(v)) },
                    grid: { display: false },
                    border: { color: c.grid }
                },
                y: {
                    beginAtZero: true, max,
                    ticks: { color: c.muted, font: { family: CHART_FONT }, maxTicksLimit: 5, callback: (v: any) => format(Number(v)) },
                    grid: { color: c.grid },
                    border: { display: false }
                }
            }
        };
    }

    cpuTitle(u: StoreEdgeUsage): string {
        return u.cpuCores ? `CPU · ${u.cpuCores} cores` : 'CPU';
    }

    memoryTitle(u: StoreEdgeUsage): string {
        return u.memoryTotalMb ? `Memory · ${this.gb(u.memoryTotalMb)} GB` : 'Memory';
    }

    readonly throughput = (kbps: number): string =>
        kbps >= 1024 ? `${(kbps / 1024).toFixed(kbps >= 10_240 ? 0 : 1)} MB/s` : `${Math.round(kbps)} KB/s`;

    /** Coarse on purpose: the question is "how long was it dark", not "how many seconds". */
    humanise(seconds: number): string {
        const s = Math.round(seconds);
        if (s < 60) return `${s}s`;
        const m = Math.round(s / 60);
        if (m < 60) return `${m}m`;
        const h = Math.floor(m / 60);
        if (h < 48) return `${h}h ${m % 60}m`;
        return `${Math.floor(h / 24)}d ${h % 24}h`;
    }

    private describe(e: any, permission: string): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the ${permission} permission.`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
